import { ArrowDownOutlined, RightOutlined } from '@ant-design/icons';
import { Pagination, Radio } from 'antd';
import commaNumber from 'comma-number';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import useExplorerPaylaod from '@/features/InsightExplorer/hooks/useExplorerPaylaod';
import useExplorerSelect from '@/features/InsightExplorer/hooks/useExplorerSelect';
import useExplorerStatistics from '@/features/InsightExplorer/hooks/useExplorerStatistics';
import useInsightExplorerFeedbackListModal from '@/features/InsightExplorer/hooks/useInsightExplorerFeedbackListModal';
import {
  InsightExplorerByType,
  InsightExplorerByTypeMap,
  InsightExplorerFieldType,
  InsightExplorerMetricType,
  InsightExplorerMetricTypeMap,
  InsightExplorerViewType,
} from '@/features/InsightExplorer/types/constants';
import type {
  InsightExplorerDistributionResponse,
  InsightExplorerTrendResponse,
} from '@/features/InsightExplorer/types/types';
import { insightExplorerUtils } from '@/features/InsightExplorer/utils/insight-explorer.utils';
import useColor from '@/hooks/domain/useColor';
import useInsightExplorerStore from '@/stores/InsightExplorerStore';
import { track } from '@/utils/track/track';

import type { SubtopicItem } from '../types';
import { useSubtopicSection } from './useSubtopicSection';

interface SubtopicsSectionProps {
  data?: InsightExplorerTrendResponse;
  childrenData: InsightExplorerDistributionResponse;
  currentPage: number;
  totalPages: number;
  metricType: InsightExplorerMetricType;
  onPageChange?: (page: number) => void;
  onMetricTypeChange: (metricType: InsightExplorerMetricType) => void;
}

const PAGE_SIZE = 5;

const SubtopicsSection = ({
  childrenData,
  currentPage,
  totalPages,
  onPageChange,
  metricType,
  onMetricTypeChange,
  data,
}: SubtopicsSectionProps) => {
  const { field_type } = useParams();
  const statistics = useExplorerStatistics({
    data,
  });
  const { view, findNode, by, selectedNode } = useInsightExplorerStore();
  const selectNode = useExplorerSelect();
  const { filterPayload } = useExplorerPaylaod();

  const { subtopicsData } = useSubtopicSection({
    view,
    childrenData,
    findNode,
    metricType,
    statistics,
    by,
    field: field_type as InsightExplorerFieldType,
    exceptSelf: selectedNode?.exceptSelf,
  });
  const colors = useColor();

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange?.(page);
    }
  };

  const slicedData = useMemo(() => {
    return subtopicsData.slice(
      (currentPage - 1) * PAGE_SIZE,
      currentPage * PAGE_SIZE,
    );
  }, [subtopicsData, currentPage]);

  const onItemClick = (item: SubtopicItem) => {
    const node = findNode(item.id);
    if (node) {
      selectNode(node);
    }
  };

  return (
    <VFlex g={16}>
      <Flex j="space-between" a="center" style={{ height: 16 }}>
        <Flex g={4} a="center">
          <ArrowDownOutlined style={{ fontSize: 12 }} />
          <Typo type="label3" weight="m" color={colors.gray800}>
            {view === InsightExplorerViewType.VOLUME
              ? 'High volume'
              : 'High negative volume'}
          </Typo>
        </Flex>
        {!selectedNode?.exceptSelf && (
          <Radio.Group
            value={metricType}
            onChange={(e) => onMetricTypeChange(e.target.value)}
          >
            {InsightExplorerMetricTypeMap.toArray().map((op) => {
              return (
                <Radio.Button key={op.value} value={op.value}>
                  {op.label}
                </Radio.Button>
              );
            })}
          </Radio.Group>
        )}
      </Flex>
      <VFlex g={10}>
        {slicedData.map((item, index) => (
          <SubtopicItemComponent
            index={index}
            key={item.id}
            isShare={metricType === InsightExplorerMetricType.PERCENTAGE}
            isNegative={view === InsightExplorerViewType.SENTIMENTS}
            item={item}
            onClick={onItemClick}
            nodeType={
              field_type === InsightExplorerFieldType.TAXONOMY
                ? InsightExplorerByType.TAXONOMY_TOPIC
                : InsightExplorerByType.TAG
            }
            view={view}
            filterPayload={filterPayload}
          />
        ))}
      </VFlex>
      {/* Pagination */}
      <Flex j="center" g={0}>
        <Pagination
          total={subtopicsData.length || 0}
          current={currentPage}
          pageSize={PAGE_SIZE}
          showSizeChanger={false}
          onChange={handlePageChange}
        />
      </Flex>
    </VFlex>
  );
};

interface SubtopicItemComponentProps {
  index: number;
  item: SubtopicItem;
  isNegative: boolean;
  isShare: boolean;
  onClick?: (item: SubtopicItem) => void;
  nodeType: InsightExplorerByType;
  view: InsightExplorerViewType;
  filterPayload: StructuredFilter;
}

const SubtopicItemComponent = ({
  index,
  item,
  onClick,
  isNegative,
  isShare,
  nodeType,
  view,
  filterPayload,
}: SubtopicItemComponentProps) => {
  const openInsightExplorerFeedbackListModal =
    useInsightExplorerFeedbackListModal();

  const colors = useColor();

  const openFeedbackModal = (expandAI?: boolean) => {
    const filter = insightExplorerUtils
      .getFilterByExplorerOptions({
        view: view,
        filterPayload: filterPayload,
        selectedNode: {
          id: item.id,
          path: [],
          name: item.name,
          node_type: nodeType,
        },
      })
      .get();

    openInsightExplorerFeedbackListModal({
      filter,
      socketInfo: {
        initialState: expandAI
          ? CollapseState.EXPANDED
          : CollapseState.COLLAPSED,
      },
    });
  };

  return (
    <SubtopicItemContainer
      isNegative={isNegative}
      onClick={() => onClick?.(item)}
    >
      <Flex
        a="center"
        g={8}
        padding="0 12px 0 0"
        style={{ position: 'relative', zIndex: 1, height: '52px' }}
      >
        <ColorIndicator bgColor={item.color} />
        <Space w={1} />
        <Typo type="label3" weight="m" color={colors.ND40}>
          {index + 1}
        </Typo>
        <Space w={2} />
        <VFlex f={1}>
          <Typo type="label2" weight="sb" color={colors.gray900}>
            {item.name}
          </Typo>
        </VFlex>
        <Flex a="center" g={4}>
          <Typo type="label3" weight="m" color={colors.gray700}>
            {isShare ? `${item.share.toFixed(1)}%` : commaNumber(item.count)}
          </Typo>
          <RightOutlined style={{ fontSize: '10px', color: colors.ND40 }} />
        </Flex>
      </Flex>
      <ShareBar
        style={{
          backgroundColor: isNegative ? colors.red(0.15) : colors.gray300,
          width: `${item.share}%`,
          zIndex: 0,
        }}
      />
      <Flex className="hover-item" g={8}>
        <Button
          variant="outlined"
          onClick={() => {
            openFeedbackModal(true);
            track(AMP_KEYS.INSIGHT_EXPLORER_CHILD_ASK_SYNCLY_CLICK, {
              node_type: InsightExplorerByTypeMap.get(nodeType),
              node_name: item.name,
            });
          }}
          style={{ backgroundColor: colors.gray100 }}
          leftIcon={<AIIcon />}
        >
          Ask Syncly AI
        </Button>
        <Button
          variant="outlined"
          style={{ backgroundColor: colors.gray100 }}
          onClick={() => onClick?.(item)}
        >
          See topic
        </Button>
      </Flex>
    </SubtopicItemContainer>
  );
};

const SubtopicItemContainer = styled.div<{
  isNegative: boolean;
}>`
  position: relative;
  background-color: ${(props) =>
    props.isNegative
      ? props.theme.colors.red(0.1)
      : props.theme.colors.gray200};
  border: 1px solid
    ${(props) =>
      props.isNegative
        ? props.theme.colors.red(0.15)
        : props.theme.colors.gray300};
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 2px;
  overflow: hidden;

  .hover-item {
    transition: all 0.2s;
    opacity: 0;
    position: absolute;
    top: 12px;
    right: 16px;
    z-index: 10;
  }

  &:hover {
    .hover-item {
      opacity: 1;
    }
  }
`;

const ShareBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: ${(props) => props.theme.colors.gray300};
  transition: all 0.2s;
`;

const ColorIndicator = styled.div<{ bgColor: string }>`
  width: 5px;
  height: 100%;
  background-color: ${(props) => props.bgColor};
`;

export default SubtopicsSection;
